import React from 'react';
import { ChevronDown, ChevronRight, Users } from 'lucide-react';
import type { EmployeeHierarchyNode, EmployeeHierarchyPerson } from '../types';

const AVATAR_TONES = [
  'bg-violet-100 text-violet-800',
  'bg-sky-100 text-sky-800',
  'bg-teal-100 text-teal-800',
  'bg-amber-100 text-amber-800',
  'bg-rose-100 text-rose-800',
  'bg-indigo-100 text-indigo-800',
];

export function countDescendants(node: EmployeeHierarchyNode): number {
  return (node.children ?? []).reduce((sum, child) => sum + 1 + countDescendants(child), 0);
}

export function countPeople(nodes: EmployeeHierarchyNode[]): number {
  return nodes.reduce((sum, node) => sum + 1 + countDescendants(node), 0);
}

export function collectExpandableIds(nodes: EmployeeHierarchyNode[]): Set<number> {
  const ids = new Set<number>();
  const walk = (node: EmployeeHierarchyNode) => {
    if ((node.children ?? []).length > 0) {
      ids.add(node.id);
      node.children.forEach(walk);
    }
  };
  nodes.forEach(walk);
  return ids;
}

export function idsExpandedToDepth(nodes: EmployeeHierarchyNode[], maxDepth: number, depth = 0): Set<number> {
  const ids = new Set<number>();
  if (depth >= maxDepth) return ids;
  for (const node of nodes) {
    if ((node.children ?? []).length > 0) {
      ids.add(node.id);
      idsExpandedToDepth(node.children, maxDepth, depth + 1).forEach((id) => ids.add(id));
    }
  }
  return ids;
}

export function collectMatchingIds(nodes: EmployeeHierarchyNode[], query: string): Set<number> {
  const needle = query.trim().toLowerCase();
  const ids = new Set<number>();
  if (!needle) return ids;
  const walk = (node: EmployeeHierarchyNode) => {
    const hay = `${node.firstName} ${node.lastName} ${node.mainPositionTitle ?? ''}`.toLowerCase();
    if (hay.includes(needle)) ids.add(node.id);
    (node.children ?? []).forEach(walk);
  };
  nodes.forEach(walk);
  return ids;
}

/** IDs that should be expanded so matching people are visible. */
export function expandIdsForMatches(nodes: EmployeeHierarchyNode[], matchIds: Set<number>): Set<number> {
  const ids = new Set<number>();
  const walk = (node: EmployeeHierarchyNode): boolean => {
    let childHit = false;
    for (const child of node.children ?? []) {
      if (walk(child)) childHit = true;
    }
    const hit = matchIds.has(node.id) || childHit;
    if (childHit) ids.add(node.id);
    return hit;
  };
  nodes.forEach(walk);
  return ids;
}

function initialsOf(person: EmployeeHierarchyPerson): string {
  return `${person.firstName?.[0] ?? ''}${person.lastName?.[0] ?? ''}`.toUpperCase() || '?';
}

function personLabel(person: EmployeeHierarchyPerson): string {
  return `${person.firstName} ${person.lastName}`.trim();
}

export const OrgChartPersonCard: React.FC<{
  person: EmployeeHierarchyPerson;
  variant?: 'default' | 'focus';
  onOpenProfile: (id: number) => void;
  reportCount?: number;
  expanded?: boolean;
  onToggleExpand?: () => void;
  highlighted?: boolean;
  dimmed?: boolean;
}> = ({
  person,
  variant = 'default',
  onOpenProfile,
  reportCount = 0,
  expanded,
  onToggleExpand,
  highlighted,
  dimmed,
}) => {
  const isFocus = variant === 'focus';
  const canExpand = Boolean(onToggleExpand) && reportCount > 0;

  return (
    <div
      className={`org-tree-card relative w-[13.5rem] rounded-xl border bg-white text-left shadow-sm transition-all ${
        isFocus
          ? 'border-violet-500 ring-2 ring-violet-400 ring-offset-1'
          : highlighted
            ? 'border-violet-400 ring-2 ring-violet-300'
            : 'border-neutral-200'
      } ${dimmed ? 'opacity-40' : ''} ${canExpand ? 'mb-3' : ''}`}
    >
      <button
        type="button"
        onClick={() => onOpenProfile(person.id)}
        className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-neutral-50 rounded-xl"
      >
        <span
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
            AVATAR_TONES[person.id % AVATAR_TONES.length]
          }`}
          aria-hidden
        >
          {initialsOf(person)}
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block text-sm font-semibold leading-snug ${isFocus ? 'text-violet-950' : 'text-neutral-900'}`}>
            {personLabel(person)}
          </span>
          <span className="mt-0.5 block text-xs text-neutral-500 line-clamp-2">
            {person.mainPositionTitle || 'No position'}
          </span>
        </span>
      </button>
      {canExpand && (
        <button
          type="button"
          onClick={onToggleExpand}
          className="absolute -bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full border border-neutral-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-neutral-600 shadow-sm hover:border-violet-300 hover:text-violet-700"
          aria-expanded={expanded}
          aria-label={expanded ? `Hide reports of ${personLabel(person)}` : `Show reports of ${personLabel(person)}`}
        >
          <Users className="h-3 w-3" />
          {reportCount}
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
      )}
    </div>
  );
};

type TreeItemProps = {
  node: EmployeeHierarchyNode;
  onOpenProfile: (id: number) => void;
  expandedIds: Set<number>;
  onToggleExpand: (id: number) => void;
  highlightedIds?: Set<number>;
  dimUnmatched?: boolean;
  focusId?: number;
};

const OrgChartTreeItem: React.FC<TreeItemProps> = ({
  node,
  onOpenProfile,
  expandedIds,
  onToggleExpand,
  highlightedIds,
  dimUnmatched,
  focusId,
}) => {
  const children = node.children ?? [];
  const reportCount = countDescendants(node);
  const expanded = expandedIds.has(node.id) && children.length > 0;
  const highlighted = highlightedIds?.has(node.id) ?? false;
  const dimmed = Boolean(dimUnmatched && highlightedIds && highlightedIds.size > 0 && !highlighted);
  const hanging = children.length > 6;

  return (
    <li>
      <OrgChartPersonCard
        person={node}
        variant={focusId === node.id ? 'focus' : 'default'}
        onOpenProfile={onOpenProfile}
        reportCount={reportCount}
        expanded={expanded}
        onToggleExpand={children.length > 0 ? () => onToggleExpand(node.id) : undefined}
        highlighted={highlighted}
        dimmed={dimmed}
      />
      {expanded && (
        <ul className={hanging ? 'org-hanging' : undefined}>
          {children.map((child) => (
            <OrgChartTreeItem
              key={child.id}
              node={child}
              onOpenProfile={onOpenProfile}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              highlightedIds={highlightedIds}
              dimUnmatched={dimUnmatched}
              focusId={focusId}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export const OrgChartTree: React.FC<{
  nodes: EmployeeHierarchyNode[];
  onOpenProfile: (id: number) => void;
  expandedIds: Set<number>;
  onToggleExpand: (id: number) => void;
  highlightedIds?: Set<number>;
  dimUnmatched?: boolean;
  focusId?: number;
  /** Draw connectors from an external parent card above the tree. */
  attachedToParent?: boolean;
  header?: React.ReactNode;
}> = ({
  nodes,
  onOpenProfile,
  expandedIds,
  onToggleExpand,
  highlightedIds,
  dimUnmatched,
  focusId,
  attachedToParent,
  header,
}) => {
  if (nodes.length === 0 && !header) return null;

  return (
    <div className={`org-tree ${attachedToParent ? 'org-tree-attached' : ''}`}>
      <ul>
        {header ? (
          <li>
            {header}
            {nodes.length > 0 && (
              <ul>
                {nodes.map((node) => (
                  <OrgChartTreeItem
                    key={node.id}
                    node={node}
                    onOpenProfile={onOpenProfile}
                    expandedIds={expandedIds}
                    onToggleExpand={onToggleExpand}
                    highlightedIds={highlightedIds}
                    dimUnmatched={dimUnmatched}
                    focusId={focusId}
                  />
                ))}
              </ul>
            )}
          </li>
        ) : (
          nodes.map((node) => (
            <OrgChartTreeItem
              key={node.id}
              node={node}
              onOpenProfile={onOpenProfile}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              highlightedIds={highlightedIds}
              dimUnmatched={dimUnmatched}
              focusId={focusId}
            />
          ))
        )}
      </ul>
    </div>
  );
};

export const OrgChartOutlineItem: React.FC<TreeItemProps> = ({
  node,
  onOpenProfile,
  expandedIds,
  onToggleExpand,
  highlightedIds,
  dimUnmatched,
  focusId,
}) => {
  const children = node.children ?? [];
  const reportCount = countDescendants(node);
  const expanded = expandedIds.has(node.id) && children.length > 0;
  const highlighted = highlightedIds?.has(node.id) ?? false;
  const dimmed = Boolean(dimUnmatched && highlightedIds && highlightedIds.size > 0 && !highlighted);
  const isFocus = focusId === node.id;

  return (
    <li>
      <div
        className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${
          isFocus ? 'bg-violet-50 ring-1 ring-violet-300' : highlighted ? 'bg-violet-50' : 'hover:bg-neutral-50'
        } ${dimmed ? 'opacity-40' : ''}`}
      >
        {children.length > 0 ? (
          <button
            type="button"
            onClick={() => onToggleExpand(node.id)}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800"
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : (
          <span className="w-6 shrink-0" aria-hidden />
        )}
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
            AVATAR_TONES[node.id % AVATAR_TONES.length]
          }`}
          aria-hidden
        >
          {initialsOf(node)}
        </span>
        <button type="button" onClick={() => onOpenProfile(node.id)} className="min-w-0 flex-1 text-left">
          <span className="block truncate text-sm font-medium text-neutral-900">{personLabel(node)}</span>
          <span className="block truncate text-xs text-neutral-500">{node.mainPositionTitle || 'No position'}</span>
        </button>
        {reportCount > 0 && (
          <span className="shrink-0 text-[11px] text-neutral-500">
            {reportCount} {reportCount === 1 ? 'report' : 'reports'}
          </span>
        )}
      </div>
      {expanded && (
        <ul className="ml-4 border-l border-neutral-200 pl-3">
          {children.map((child) => (
            <OrgChartOutlineItem
              key={child.id}
              node={child}
              onOpenProfile={onOpenProfile}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              highlightedIds={highlightedIds}
              dimUnmatched={dimUnmatched}
              focusId={focusId}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export const OrgChartOutline: React.FC<{
  nodes: EmployeeHierarchyNode[];
  onOpenProfile: (id: number) => void;
  expandedIds: Set<number>;
  onToggleExpand: (id: number) => void;
  highlightedIds?: Set<number>;
  dimUnmatched?: boolean;
  focusId?: number;
}> = ({ nodes, onOpenProfile, expandedIds, onToggleExpand, highlightedIds, dimUnmatched, focusId }) => (
  <ul className="space-y-0.5">
    {nodes.map((node) => (
      <OrgChartOutlineItem
        key={node.id}
        node={node}
        onOpenProfile={onOpenProfile}
        expandedIds={expandedIds}
        onToggleExpand={onToggleExpand}
        highlightedIds={highlightedIds}
        dimUnmatched={dimUnmatched}
        focusId={focusId}
      />
    ))}
  </ul>
);

/** @deprecated Use OrgChartTree — kept for a single-node attached branch. */
export const OrgChartBranch: React.FC<{
  node: EmployeeHierarchyNode;
  onOpenProfile: (id: number) => void;
}> = ({ node, onOpenProfile }) => {
  const expandedIds = collectExpandableIds([node]);
  return (
    <OrgChartTree
      nodes={[node]}
      onOpenProfile={onOpenProfile}
      expandedIds={expandedIds}
      onToggleExpand={() => undefined}
    />
  );
};
